import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  ListSubheader,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LabelIcon from '@mui/icons-material/Label';
import { PDFPropertiesPanelProps } from './types';

const GROUP_ICONS: Record<string, React.ReactNode> = {
  'Clients': <PersonIcon fontSize="small" />,
  'Bank Accounts': <AccountBalanceIcon fontSize="small" />,
  'Companies': <BusinessIcon fontSize="small" />,
  'Transactions': <ReceiptLongIcon fontSize="small" />,
};

export const PDFPropertiesPanel: React.FC<PDFPropertiesPanelProps> = ({
  popupState,
  currentPage,
  currentPagePlaceholders,
  selectedPlaceholder,
  selectedPlaceholderId,
  dbColumns,
  filteredGroupedColumns,
  columnSearch,
  staticTextInput,
  customVariableInput,
  onCustomVariableInputChange,
  onCreateCustomVariable,
  onPopupModeChange,
  onClosePopup,
  onColumnSearchChange,
  onSelectColumnInPopup,
  onStaticTextInputChange,
  onCreateStaticText,
  onSelectPlaceholder,
  onUpdatePlaceholder,
  onDeletePlaceholder,
  getColumnDisplayName,
}) => {
  return (
    <Paper sx={{ width: 320, p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {popupState && (
        <Box sx={{
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
          p: 1.5,
          backgroundColor: 'primary.50',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <ToggleButtonGroup
              value={popupState.mode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode) {
                  onPopupModeChange(newMode);
                }
              }}
              size="small"
            >
              <ToggleButton value="variable" sx={{ px: 1.5 }}>
                <DataObjectIcon fontSize="small" sx={{ mr: 0.5 }} />
                Variable
              </ToggleButton>
              <ToggleButton value="static_text" sx={{ px: 1.5 }}>
                <TextFieldsIcon fontSize="small" sx={{ mr: 0.5 }} />
                Static Text
              </ToggleButton>
              <ToggleButton value="custom_variable" sx={{ px: 1.5 }}>
                <LabelIcon fontSize="small" sx={{ mr: 0.5 }} />
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" onClick={onClosePopup}>Cancel</Button>
          </Box>

          {popupState.mode === 'variable' && popupState.step === 'columns' && (
            <>
              <TextField
                size="small"
                placeholder="Search columns..."
                fullWidth
                value={columnSearch}
                onChange={e => onColumnSearchChange(e.target.value)}
                autoFocus
                sx={{ mb: 1 }}
              />
              <List dense sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'white', borderRadius: 1 }}>
                {Object.entries(filteredGroupedColumns).map(([group, columns]) => (
                  <React.Fragment key={group}>
                    <ListSubheader sx={{ bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 1, lineHeight: '32px' }}>
                      {GROUP_ICONS[group] || null}
                      {group}
                    </ListSubheader>
                    {columns.map(col => (
                      <ListItemButton
                        key={col.key}
                        onClick={() => {
                          onColumnSearchChange('');
                          onSelectColumnInPopup(col.key);
                        }}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemText primary={col.display} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                        <ChevronRightIcon fontSize="small" color="action" />
                      </ListItemButton>
                    ))}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}

          {popupState.mode === 'static_text' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter fixed text to insert into the document:
              </Typography>
              <TextField
                size="small"
                placeholder="Enter text..."
                fullWidth
                value={staticTextInput}
                onChange={e => onStaticTextInputChange(e.target.value)}
                autoFocus
                multiline
                rows={2}
                sx={{ mb: 1.5 }}
              />
              <Button
                variant="contained"
                fullWidth
                disabled={!staticTextInput.trim()}
                onClick={() => onCreateStaticText(staticTextInput.trim())}
              >
                Add Text
              </Button>
            </>
          )}

          {popupState.mode === 'custom_variable' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Create a custom variable. Its value will be filled manually during document generation.
              </Typography>
              <TextField
                size="small"
                placeholder="Variable name..."
                fullWidth
                value={customVariableInput}
                onChange={e => onCustomVariableInputChange(e.target.value)}
                autoFocus
                sx={{ mb: 1.5 }}
              />
              <Button
                variant="contained"
                fullWidth
                disabled={!customVariableInput.trim()}
                onClick={() => onCreateCustomVariable(customVariableInput.trim())}
              >
                Add Variable
              </Button>
            </>
          )}
        </Box>
      )}

      {!popupState && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Placeholders (Page {currentPage})
          </Typography>

          {currentPagePlaceholders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Click on the PDF to add a variable or static text. Drag to move.
            </Typography>
          ) : (
            <List dense>
              {currentPagePlaceholders.map(p => {
                const isStaticText = p.type === 'static_text';
                const isCustomVariable = p.type === 'custom_variable';
                const icon = isCustomVariable ? <LabelIcon fontSize="small" /> : isStaticText ? <TextFieldsIcon fontSize="small" /> : <DataObjectIcon fontSize="small" />;
                const primaryText = isCustomVariable
                  ? (p.custom_variable_name || 'Custom Variable')
                  : isStaticText
                    ? (p.static_value || 'Static Text')
                    : getColumnDisplayName(p.db_column);
                const secondaryText = isCustomVariable
                  ? 'Custom Variable'
                  : isStaticText
                    ? 'Static Text'
                    : (p.currentValue || 'No value');
                return (
                  <ListItem
                    key={p.id}
                    sx={{
                      backgroundColor: selectedPlaceholderId === p.id ? 'primary.light' : undefined,
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => onSelectPlaceholder(p.id)}
                  >
                    <Box sx={{ mr: 1, color: 'text.secondary' }}>
                      {icon}
                    </Box>
                    <ListItemText
                      primary={primaryText}
                      secondary={secondaryText}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem', noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeletePlaceholder(p.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      )}

      {selectedPlaceholder && (
        <Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Properties
          </Typography>

          {selectedPlaceholder.type === 'custom_variable' ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Variable Name</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedPlaceholder.custom_variable_name || 'Unnamed'}
              </Typography>
            </Box>
          ) : selectedPlaceholder.type === 'static_text' ? (
            <TextField
              label="Text Value"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={selectedPlaceholder.static_value || ''}
              onChange={(e) => onUpdatePlaceholder(selectedPlaceholder.id, {
                static_value: e.target.value,
                currentValue: e.target.value
              })}
              sx={{ mb: 2 }}
            />
          ) : (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Column</InputLabel>
                <Select
                  value={selectedPlaceholder.db_column}
                  label="Column"
                  onChange={(e) => onUpdatePlaceholder(selectedPlaceholder.id, { db_column: e.target.value })}
                >
                  {dbColumns.map(col => (
                    <MenuItem key={col} value={col}>{getColumnDisplayName(col) || col}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedPlaceholder.currentValue && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Current Value</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedPlaceholder.currentValue}
                  </Typography>
                </Box>
              )}
            </>
          )}

          <Box sx={{ mb: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Position (drag to move)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              X: {Math.round(selectedPlaceholder.rect.x)}pt, Y: {Math.round(selectedPlaceholder.rect.y)}pt
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              Size: {Math.round(selectedPlaceholder.rect.w)}{Math.round(selectedPlaceholder.rect.h)}pt
            </Typography>
          </Box>

          <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Detected Font (auto)
            </Typography>
            <Typography variant="body2">
              Font: {selectedPlaceholder.style.font_name}
            </Typography>
            <Typography variant="body2">
              Size: {selectedPlaceholder.style.font_size}pt
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Color:</Typography>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: selectedPlaceholder.style.color,
                  border: '1px solid #ccc',
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {selectedPlaceholder.style.color}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
